import React, { memo, useCallback } from "react";
import colors from "app/colors";

import { TrashOutlineIcon } from "modules/icons";
import {
  InternalAdminDistributionGroupFragment,
  InternalAdminEditDistributionGroupMutation,
  InternalAdminEditDistributionGroupMutationVariables,
} from "gql/graphql";
import { useMutation } from "@apollo/client";
import { Button } from "@chakra-ui/react";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/internal-admin-edit-distribution-group";

interface IProps {
  distributionGroup?: InternalAdminDistributionGroupFragment;
  onDelete?: () => void;
}

const InternalAdminDistributionGroupDeleteQueryButton = ({
  distributionGroup,
  onDelete,
}: IProps) => {
  const { openModal } = useOverlay();
  const [internalAdminEditDistributionGroupMutationAPI] = useMutation<
    InternalAdminEditDistributionGroupMutation,
    InternalAdminEditDistributionGroupMutationVariables
  >(INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const deleteDynamicRules = useCallback(() => {
    if (!distributionGroup) return;
    internalAdminEditDistributionGroupMutationAPI({
      variables: {
        organizationId: distributionGroup.organizationId,
        id: distributionGroup.id,
        input: {
          query: null,
          queryBuilder: null,
        },
      },
      fetchPolicy: "network-only",
      onCompleted: () => {
        if (onDelete) onDelete();
      },
    });
  }, [
    distributionGroup,
    internalAdminEditDistributionGroupMutationAPI,
    onDelete,
  ]);

  const onClick = useCallback(() => {
    if (!distributionGroup) return;
    openModal({
      type: MODAL_TYPE.CONFIRM,
      onSaveCallback: deleteDynamicRules,
      helpModal: {
        icon: <TrashOutlineIcon boxSize={14} fill={colors.black} />,
        title: "Delete Dynamic Rules",
        message:
          "Are you sure you want to delete dynamic rules for this group? This action cannot be undone.",
      },
    });
  }, [deleteDynamicRules, distributionGroup, openModal]);

  if (!distributionGroup || distributionGroup.query === null) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      alignItems="center"
      fontSize={12}
      fontWeight={600}
      color={colors.gray40}
      size="xs"
      leftIcon={<TrashOutlineIcon boxSize={4} fill={colors.gray40} />}
      onClick={onClick}
    >
      Delete Rules
    </Button>
  );
};

export default memo(InternalAdminDistributionGroupDeleteQueryButton);
